import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Calendar, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { EXPERIENCE } from '../mock/data';

const Experience = () => {
  const { t } = useLanguage();

  return (
    <section id="experience" className="section relative" style={{ background: 'linear-gradient(180deg, #0A0E1A 0%, #0c1220 55%, #0A0E1A 100%)' }}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="label-small mb-4">{t.experience.label}</div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
            {t.experience.heading}
          </h2>
          <div className="gold-rule mt-6 mb-6" />
          <p className="text-[#DCCFAD] text-lg italic font-heading">
            {t.experience.subheading}
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="mt-16 relative">
          {/* Vertical rule (desktop) */}
          <div
            className="hidden md:block absolute top-0 bottom-0 w-[1px]"
            style={{
              left: '160px',
              background: 'linear-gradient(180deg, transparent, rgba(196,163,90,0.4) 8%, rgba(196,163,90,0.4) 92%, transparent)',
            }}
          />

          <div className="space-y-10">
            {EXPERIENCE.map((exp, i) => (
              <motion.article
                key={exp.id}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="relative grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 md:gap-10"
              >
                {/* Date column */}
                <div className="md:pr-6 md:text-right relative">
                  {/* Dot on the rule */}
                  <div
                    className="hidden md:block absolute right-[-6px] top-2 w-3 h-3 rounded-full"
                    style={{
                      background: '#C4A35A',
                      boxShadow: '0 0 0 3px #0A0E1A, 0 0 0 4px rgba(196,163,90,0.35)',
                    }}
                  />
                  <div className="flex md:justify-end items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C4A35A]">
                    <Calendar size={11} />
                    <span>{exp.dates}</span>
                  </div>
                  <div className="mt-2 flex md:justify-end items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-[#8a7a54]">
                    <MapPin size={10} />
                    <span>{exp.location}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="border-l border-[rgba(196,163,90,0.2)] md:border-l-0 pl-4 md:pl-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <Briefcase size={14} className="text-[#C4A35A]" />
                    <span className="text-[10px] tracking-[0.35em] uppercase" style={{ color: '#C4A35A' }}>
                      {exp.tag}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl text-[#F0EDE8] leading-tight">
                    {exp.role}
                  </h3>
                  <div className="font-heading italic text-[#DCCFAD] mt-1">
                    {exp.org}
                  </div>

                  <ul className="mt-5 space-y-2.5">
                    {exp.highlights.map((h, k) => (
                      <li key={k} className="flex items-start gap-3 text-[#DCCFAD] text-[15px] leading-relaxed">
                        <ChevronRight size={14} className="text-[#C4A35A] mt-1.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
